import { BANNED_NUMBERS } from "logic/Consts";
import { db } from "firebaseConfig";
import type {
  RealEstateContact,
  ProcessedListingData,
  ProcessedListingAddress
} from "../../../@types/sharedSchema";

/**
 * WHAT THE BULK-MESSAGE DIALOG READS FROM A RECIPIENT — and nothing else.
 *
 * The identity it sends to, the fields a template can print (`{{שם}}`, rooms, notes), and
 * the three facts the skip rules test (opted out, spam, messaged recently).
 *
 * It is a type of its own, rather than `RealEstateContact`, because a recipient is not
 * always a contact document: an audience member, a listing seller and a looked-up
 * `ContactSummary` all drive this dialog, and each used to be cast to the full contact type
 * to get in. Naming the fields that are read is what lets all of them — and a whole
 * contact — be passed as they are, and makes a template field that nothing supplies a
 * compile error instead of a placeholder that silently renders empty.
 */
export type BulkMessageRecipient = Pick<RealEstateContact, "id"> &
  Partial<
    Pick<
      RealEstateContact,
      | "callerFirstName"
      | "callerLastName"
      | "contactFullName"
      | "roomsMin"
      | "roomsMax"
      | "additionalNotes"
      | "lastBulkMessageReceivedTime"
      | "optedOutOfBulkMessages"
      | "isInSpamDatabase"
    >
  >;

export type MessageStatus = {
  data: BulkMessageRecipient;
  status: MessageStatusKey;
  customMessage?: string;
  customTooltip?: string;
};

export type MessageStatusKey = "pending" | "skipped" | "loading" | "warning" | "success" | "error";

export type SelectedMessageStatus = Record<string, MessageStatus>;

export function setInitialSelectedMessageStatus(
  selected: string[],
  audience: BulkMessageRecipient[],
  filterRecentContactsBulkMessages: boolean
): SelectedMessageStatus {
  const status: SelectedMessageStatus = {};

  // Indexed once: the old page hands this thousands of selected ids against a book of tens
  // of thousands, and a scan per id was a product of the two.
  const audienceById = new Map(audience.map((recipient) => [recipient.id, recipient]));

  for (const contactId of selected) {
    // The id IS the phone — a stand-in recipient needs nothing else to be identified.
    let data: BulkMessageRecipient = { id: contactId } as BulkMessageRecipient;

    const contact = audienceById.get(contactId);

    if (contact) data = contact;

    let messageStatus: MessageStatus = {
      data: data,
      status: "pending"
    };

    const skipContactReasons = getSkipContactReasons(contact, filterRecentContactsBulkMessages);

    if (skipContactReasons.length) {
      messageStatus = {
        data: data,
        status: "skipped",
        customTooltip: skipContactReasons[0]
      };
    }

    status[contactId] = messageStatus;
  }

  return status;
}

export type SkipContactReason =
  | "numberHasReceivedMessageInLast30Days"
  | "numberOptedOutOfBulkMessages"
  | "numberIsInSpamDatabase"
  | "numberIsInBannedNumbersList";

export function getSkipContactReasons(
  contact: BulkMessageRecipient | undefined,
  filterRecentContactsBulkMessages: boolean = false
): SkipContactReason[] {
  const lastBulkMessageReceivedTime = contact?.lastBulkMessageReceivedTime;

  let isContactReceivedMessage30DaysAgo = false;

  if (filterRecentContactsBulkMessages) {
    const lastBulkMessageReceivedTimeDate = lastBulkMessageReceivedTime
      ? new Date(lastBulkMessageReceivedTime)
      : null;

    if (lastBulkMessageReceivedTimeDate) {
      const last30DaysDate = new Date();

      last30DaysDate.setDate(last30DaysDate.getDate() - 30);

      isContactReceivedMessage30DaysAgo = lastBulkMessageReceivedTimeDate > last30DaysDate;
    }
  }

  const isContactOptedOutBulkMessages = contact?.optedOutOfBulkMessages === true;
  const isContactIsInSpamDatabase = contact?.isInSpamDatabase === true;
  const isContactInBannedNumbers = BANNED_NUMBERS.includes(contact?.id);

  const reasons: SkipContactReason[] = [];

  if (isContactReceivedMessage30DaysAgo) reasons.push("numberHasReceivedMessageInLast30Days");
  if (isContactOptedOutBulkMessages) reasons.push("numberOptedOutOfBulkMessages");
  if (isContactIsInSpamDatabase) reasons.push("numberIsInSpamDatabase");
  if (isContactInBannedNumbers) reasons.push("numberIsInBannedNumbersList");

  return reasons;
}

// Cross-checks the `sentMessages` log directly by recipient phone. Catches
// recipients that are not in the contact list (so they have no
// lastBulkMessageReceivedTime to filter on) and any drift between the contact
// doc and the message log. Defaults to fail-closed: on query error, we report
// "recent message exists" so the sender doesn't accidentally re-message.
export async function hasRecentBulkMessageInLog(
  userEmail: string,
  recipientPhone: string,
  daysWindow: number = 30
): Promise<{ hasRecent: boolean; failed: boolean }> {
  try {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - daysWindow);

    const snapshot = await db
      .collection("dashboard-users-v2")
      .doc(userEmail)
      .collection("sentMessages")
      .where("sentTo", "==", recipientPhone)
      .limit(50)
      .get();

    if (snapshot.empty) return { hasRecent: false, failed: false };

    const hasRecent = snapshot.docs.some((doc) => {
      const sentTime = doc.data()?.sentTime;
      if (!sentTime) return false;
      const date = new Date(sentTime);
      return !isNaN(date.getTime()) && date >= cutoff;
    });

    return { hasRecent, failed: false };
  } catch (err) {
    console.error("hasRecentBulkMessageInLog query failed for", recipientPhone, err);
    return { hasRecent: true, failed: true };
  }
}

export type BulkMessageTemplateType = "contact" | "listing" | "custom";

export const BULK_MESSAGE_STEPS = ["writeMessage", "sendMessage"] as const;

export type BulkMessageStep = (typeof BULK_MESSAGE_STEPS)[number];

export type BulkMessageContactTemplateKey = keyof Pick<
  RealEstateContact,
  "roomsMin" | "roomsMax" | "callerFirstName" | "callerLastName" | "additionalNotes"
>;

export const BULK_MESSAGE_CONTACT_TEMPLATE_KEYS: BulkMessageContactTemplateKey[] = [
  "roomsMin",
  "roomsMax",
  "callerFirstName",
  "callerLastName",
  "additionalNotes"
];

export type BaseAddressComponentKey = keyof Pick<
  ProcessedListingAddress,
  "street" | "neighborhood" | "city"
>;

export type BaseBulkMessageListingTemplateKey = keyof Pick<
  ProcessedListingData,
  "buildingTotalFloors" | "roomsCount"
>;

export type CustomBulkMessageListingTemplateKey = "listingLink";

export type BulkMessageListingTemplateKey =
  | BaseAddressComponentKey
  | BaseBulkMessageListingTemplateKey
  | CustomBulkMessageListingTemplateKey;

export const BULK_MESSAGE_LISTING_TEMPLATE_KEYS: BulkMessageListingTemplateKey[] = [
  "city",
  "street",
  "roomsCount",
  "listingLink",
  "neighborhood",
  "buildingTotalFloors"
];

export type CustomBulkMessageContactTemplateKey = "minisiteLink";

export const CUSTOM_BULK_MESSAGE_CONTACT_TEMPLATE_KEYS: CustomBulkMessageContactTemplateKey[] = [
  "minisiteLink"
];

export type PlaceholderTemplateForm = Record<BulkMessageContactTemplateKey, string>;

export function setInitialPlaceholderTemplateForm(): PlaceholderTemplateForm {
  return {} as PlaceholderTemplateForm;
}

export type MissingFieldContactsCount = Record<BulkMessageContactTemplateKey, number>;

export const ALLOWED_FILE_MIME_TYPES = [
  // Images
  "image/png", // .png
  "image/gif", // .gif
  "image/jpeg", // .jpg, .jpeg
  "image/vnd.adobe.photoshop", // .psd

  // Documents
  "application/pdf", // .pdf
  "application/illustrator", // .ai, .eps
  "application/msword", // .doc
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/vnd.ms-excel", // .xls
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "application/vnd.ms-powerpoint", // .ppt
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx

  // Videos
  "video/x-msvideo", // .avi
  "video/mp4", // .mp4
  "video/mpeg", // .mpeg, .mpg
  "video/webm", // .webm
  "video/3gp" // .3gp
] as const;

export const IMAGE_EXTENSIONS = ["png", "gif", "jpeg", "jpg"] as const;

export type ImageExtension = (typeof IMAGE_EXTENSIONS)[number];

export const VIDEO_EXTENSIONS = ["avi", "mp4", "mpeg", "mpg", "webm", "3gp"] as const;

export type VideoExtension = (typeof VIDEO_EXTENSIONS)[number];

export type AllBulkMessageTemplateKeys =
  | BulkMessageContactTemplateKey
  | BulkMessageListingTemplateKey
  | CustomBulkMessageContactTemplateKey;

export type FieldTemplate = {
  id: AllBulkMessageTemplateKeys;
  type: BulkMessageTemplateType;
  active: boolean;
  placeholder: string;
};

export type ActiveFieldTemplate = Record<AllBulkMessageTemplateKeys, FieldTemplate>;

export const initialActiveTemplates: ActiveFieldTemplate = {
  roomsMin: {
    id: "roomsMin",
    type: "contact",
    active: false,
    placeholder: ""
  },
  roomsMax: {
    id: "roomsMax",
    type: "contact",
    active: false,
    placeholder: ""
  },
  callerFirstName: {
    id: "callerFirstName",
    type: "contact",
    active: true, // Enable by default
    placeholder: ""
  },
  callerLastName: {
    id: "callerLastName",
    type: "contact",
    active: true, // Enable by default
    placeholder: ""
  },
  additionalNotes: {
    id: "additionalNotes",
    type: "contact",
    active: false,
    placeholder: ""
  },
  city: {
    id: "city",
    type: "listing",
    active: false,
    placeholder: ""
  },
  street: {
    id: "street",
    type: "listing",
    active: false,
    placeholder: ""
  },
  neighborhood: {
    id: "neighborhood",
    type: "listing",
    active: false,
    placeholder: ""
  },
  roomsCount: {
    id: "roomsCount",
    type: "listing",
    active: false,
    placeholder: ""
  },
  buildingTotalFloors: {
    id: "buildingTotalFloors",
    type: "listing",
    active: false,
    placeholder: ""
  },
  listingLink: {
    id: "listingLink",
    type: "listing",
    active: false,
    placeholder: ""
  },
  minisiteLink: {
    id: "minisiteLink",
    type: "contact",
    active: false,
    placeholder: ""
  }
};

// ---------------------------------------------------------------------------
// Custom template fields — caller-defined per-contact data
// ---------------------------------------------------------------------------

/** A custom template field definition provided by the caller of the dialog */
export type CustomTemplateField = {
  id: string;
  label: string;
  active?: boolean;
};

/** Per-contact custom data: contactId → { fieldId → value } */
export type PerContactData = Record<string, Record<string, string>>;

export interface BulkMessageDelaySettings {
  delayMinSeconds: number;
  delayMaxSeconds: number;
  cooldownEnabled: boolean;
  cooldownAfterCount: number;
  cooldownDurationSeconds: number;
}

export const DEFAULT_DELAY_SETTINGS: BulkMessageDelaySettings = {
  delayMinSeconds: 20,
  delayMaxSeconds: 80,
  cooldownEnabled: true,
  cooldownAfterCount: 50,
  cooldownDurationSeconds: 600
};

/**
 * `waiting`: the run is paused until WhatsApp Web is ready again (closed, asleep, logged out, open in
 * another window...). Nothing is skipped meanwhile; `message` says why and what to do.
 * `stopped`: the run stopped itself because sending kept failing in WhatsApp; `message` says so.
 */
export type DelayStatusType = "idle" | "delaying" | "cooldown" | "waiting" | "stopped";

/**
 * Sends in a row that failed in WhatsApp itself or may not have gone out (`unknown`), before a run stops
 * instead of going through the list.
 */
export const MAX_CONSECUTIVE_SEND_FAILURES = 3;

/**
 * How far before an attempt that may have gone out (`unknown`) the extension looks in the chat on a
 * resend (`dedupeSince`). Generous on purpose: the attempt time is this computer's clock and WhatsApp's
 * message times are not (a customer's clock was 15 minutes off, 2026-10-27), and the same text to the same
 * person within it is that attempt.
 */
export const DEDUPE_LOOKBACK_SECONDS = 20 * 60;

export interface DelayStatus {
  type: DelayStatusType;
  remainingSeconds: number;
  message?: string;
}

export function formatMinutesSeconds(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export function getMissingFieldContactsCount(
  selected: string[],
  audience: BulkMessageRecipient[]
): MissingFieldContactsCount {
  const counts: MissingFieldContactsCount = BULK_MESSAGE_CONTACT_TEMPLATE_KEYS.reduce(
    (acc, key) => {
      acc[key] = 0;
      return acc;
    },
    {} as MissingFieldContactsCount
  );

  const selectedIds = new Set(selected);
  const selectedContactObjects = audience.filter((contact) => selectedIds.has(contact.id));

  for (const contact of selectedContactObjects) {
    for (const key of BULK_MESSAGE_CONTACT_TEMPLATE_KEYS) {
      if (contact[key]) continue;
      counts[key]++;
    }
  }

  return counts;
}
